
import { useCallback, Fragment, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import wellknown from "wellknown"; // Library to convert WKT to GeoJSON
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { getAuth, signOut } from 'firebase/auth';
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useAuth } from '../contexts/AuthContext';
import { TextField, useTheme, Typography, Tooltip } from '@mui/material';
import { MapPin, ClipboardList } from 'lucide-react';
import TreeList from "../Components/Sitepage/Treelist";
import BASE_URL from '../config';
import '../styles/index.css';
import { useOrg } from "../contexts/OrgContext";
import Button from "@mui/material/Button";
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { toast } from 'react-toastify';
import ShowButton from "../Components/UI/Button";
import { usePermissions } from "../contexts/PermissionContext";
import { FaSpinner } from "react-icons/fa";
import UnifiedTaskModal from "../Components/Task/UnifiedTaskModal";
import CreateTaskDialog from '../Components/Task/CreateTaskDialog';
import GridTaskPanel from '../Components/ecological/GridTaskPanel';
import SiteOpsCard from '../Components/Task/SiteOpsCard';
import { Park, GridOn, Build } from '@mui/icons-material';
import {
  getSiteBarSubSectionSx,
  getSiteSelectionDivSx,
  getLabelMinimizeDivSx,
  getLabelTextSx,
  getLabelIconSx,
  getTreeDetailsParentSx,
  getTreeHeadingDivSx,
  getTreesTextSx,
  getStatCardEcologicalSx,
  getStatInfoDivSx,
  getStatLabelSx,
  getStatValueSx,
  getStatIconWrapperSx,
  getWorkOrderHeaderSx,
  getWorkOrderFiltersSx,
  getWorkOrderSearchSx,
  getWorkOrderListSx
} from '../Components/ecological/EcologicalMapStyles';
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;


function Ecological() {
  const theme = useTheme();
  const { can } = usePermissions();
  const { selectedOrgId } = useOrg();
  const [showDataModal, setShowDataModal] = useState(false);
  const dueDateRef = useRef();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteOptions, setsiteOptions] = useState([]);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTrees: 0,
    canopyCover: '',
    avgTreeHeight: 0,
    totalWaterBodies: 0,
    totalWaterArea: 0,
    newTreesPlanted: 0, // 👈 add this
    carbon: 0,
    treesRemoved: 0,
    naturalCapital: 0,
    siteArea: 0,

  });

  const [counts, setCounts] = useState({
    Risk: 0,
    Average: 0,
    Good: 0
  });
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const { getToken } = useAuth();
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true);
  const [tasks, setTasks] = useState([]);


  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState([39.87640775727515, 21.383192226696103]); // Default center [lng, lat]
  const statsSeqRef = useRef(0);
  const siterunsSeqRef = useRef(0);
  const subSitesSeqRef = useRef(0);


  // Timestamp selection states
  const [availableTimestamps, setAvailableTimestamps] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [selectedTimestamp, setSelectedTimestamp] = useState('');
  const [loadingTimestamps, setLoadingTimestamps] = useState(false);

  const [sites, setSites] = useState([]); // Stores fetched sites
  const [selectedSite, setSelectedSite] = useState(""); // Selected site
  const [selectedSiteCode, setSelectedSiteCode] = useState(""); // Store site code
  const [selectedSiteDetails, setSelectedSiteDetails] = useState(null);

  // Sub-site states
  const [subSites, setSubSites] = useState([]); // Sub-sites for selected site
  const [selectedSubSiteId, setSelectedSubSiteId] = useState(""); // Selected sub-site ID
  const [selectedSubSiteName, setSelectedSubSiteName] = useState(""); // Selected sub-site name
  const [polygonOpacity, setPolygonOpacity] = useState(1);



  const [selectedFilter, setSelectedFilter] = useState("All");
  const [originalTreeData, setOriginalTreeData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [isSiteDetailsPanelMinimized, setIsSiteDetailsPanelMinimized] = useState(false);
  const [isWorkOrderPanelMinimized, setIsWorkOrderPanelMinimized] = useState(false);

  // 🔲 Grid Task Panel states
  const [viewMode, setViewMode] = useState('trees'); // 'trees' or 'grids'
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  const handleGridClickRef = useRef(null);
  const [grids, setGrids] = useState([]);
  const [maintenanceOverlay, setMaintenanceOverlay] = useState({}); // grid_id -> { tint, plan_name, next_due, last_visit_outcome, last_visit_date }
  const [selectedGridIds, setSelectedGridIds] = useState([]);
  const [gridsLoading, setGridsLoading] = useState(false);

  // 🌳 Selected trees from TreeList (for grid-based task creation)
  const [selectedTreeIds, setSelectedTreeIds] = useState([]);
  const [selectedTreesData, setSelectedTreesData] = useState([]);

  // 🔲 Grid Task Details Modal state
  const [gridTaskModalOpen, setGridTaskModalOpen] = useState(false);
  const [selectedGridTaskId, setSelectedGridTaskId] = useState(null);
  const [selectedGridForTask, setSelectedGridForTask] = useState(null);
  const [highlightedTrees, setHighlightedTrees] = useState([]);


  const currentSite = {
    name: selectedSite,
    id: selectedSiteCode,  // site_id is the same as site code
    code: selectedSiteCode
  };


  useEffect(() => {
    // Fetch only eco from backend
    fetch(`${BASE_URL}/api/eco-sites`)
      .then(res => res.json())
      .then(data => setsiteOptions(data))
      .catch(err => console.error('Failed to fetch lake options:', err));
  }, []);


  // 🔹 Task Modal states (now uses UnifiedTaskModal)
  const [selectedWorkOrderTaskId, setSelectedWorkOrderTaskId] = useState(null);
  const [isWorkOrderTaskModalOpen, setIsWorkOrderTaskModalOpen] = useState(false);

  const handleRowClick = (item, event, type = "task") => {
    console.log("Row clicked:", item, type);
    // Open UnifiedTaskModal with the task ID - it will fetch its own data
    setSelectedWorkOrderTaskId(item.id);
    setIsWorkOrderTaskModalOpen(true);
  };

  const handleCloseWorkOrderTaskModal = () => {
    setIsWorkOrderTaskModalOpen(false);
    setSelectedWorkOrderTaskId(null);
    // Clear highlighted trees - map source will be cleared
    const map = mapRef.current;
    if (map && map.getSource('highlighted-task-trees')) {
      map.getSource('highlighted-task-trees').setData({
        type: 'FeatureCollection',
        features: []
      });
    }
    setHighlightedTrees([]);
  };


  // const [stats, setStats] = useState(null);
  const healthMap = {
    Risk: 'low',
    Average: 'medium',
    Good: 'high'
  };


  // Fetch stats with debounce
  useEffect(() => {
    if (!selectedSiteCode) return;

    const currentHealth = selectedFilter === 'All' ? '' : healthMap[selectedFilter];

    const timer = setTimeout(() => {
      fetchStats(currentHealth);
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedSiteCode, selectedSubSiteId, selectedFilter]);


  const handleFilterClick = (filterType) => {
    const healthValue = healthMap[filterType];
    setSelectedFilter(filterType); // triggers useEffect that will call fetchStats

    const params = new URLSearchParams();
    if (filterType !== 'All') params.append('health', healthValue);
    if (selectedRunId) params.append('run_id', selectedRunId);

    const url = `${BASE_URL}/api/trees/filter${params.toString() ? `?${params.toString()}` : ''}`;

    getToken()
      .then(token => fetch(url, { headers: { Authorization: `Bearer ${token}` } }))
      .then(res => res.json())
      .then(data => {
        console.log("Fetched filter data:", data);
        if (Array.isArray(data)) {
          setTreeData(data);
          if (filterType === 'All') setOriginalTreeData(data);
        } else if (Array.isArray(data.data)) {
          setTreeData(data.data);
          if (filterType === 'All') setOriginalTreeData(data.data);
        } else {
          console.error("Unexpected format from backend:", data);
        }
      })
      .catch(err => console.error('Error fetching filtered data:', err));
  };

  const fetchStats = async (healthFilter = '', siteCodeParam = selectedSiteCode, runIdParam = selectedRunId) => {
    setLoading(true);
    const seq = ++statsSeqRef.current;

    const token = await getToken();

    if (!token) {
      setLoading(false);
      return;
    }

    if (token) {
      try {
        // 1. Existing /api/stats call (tree counts, canopy cover, height)
        let url = `${BASE_URL}/api/stats`;
        const params = new URLSearchParams();

        if (healthFilter) {
          params.append('health', healthFilter);
        }

        if (selectedSubSiteId) {
          params.append('sub_site_id', selectedSubSiteId);
        } else if (siteCodeParam) {
          params.append('site_id', siteCodeParam);
        }

        if (runIdParam) {
          params.append('run_id', runIdParam);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        console.log("Fetching stats for:", { site: siteCodeParam, health: healthFilter, run_id: runIdParam });
        console.log("Stats fetch URL:", url);

        // 2. /api/site-metrics/latest call (carbon, natural capital, greenness, temp)
        const metricsParams = new URLSearchParams();
        if (selectedOrgId) metricsParams.append('organisation_id', selectedOrgId);
        if (selectedSubSiteId) {
          metricsParams.append('sub_site_id', selectedSubSiteId);
        } else if (siteCodeParam) {
          metricsParams.append('site_id', siteCodeParam);
        }
        const metricsUrl = `${BASE_URL}/api/site-metrics/latest${metricsParams.toString() ? '?' + metricsParams.toString() : ''}`;

        const [statsRes, metricsRes] = await Promise.all([
          fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
          metricsParams.toString()
            ? fetch(metricsUrl, { headers: { Authorization: `Bearer ${token}` } })
            : Promise.resolve(null),
        ]);

        if (!statsRes.ok) throw new Error(`Stats API returned ${statsRes.status}`);
        const data = await statsRes.json();

        let mergedStats = { ...data };

        if (metricsRes && metricsRes.ok) {
          const metricsData = await metricsRes.json();
          if (metricsData) {
            mergedStats = {
              ...mergedStats,
              carbon: metricsData.carbon_tc != null ? `${Number(metricsData.carbon_tc).toFixed(2)} tC` : mergedStats.carbon,
              naturalCapital: metricsData.natural_capital_usd != null
                ? `$${Number(metricsData.natural_capital_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : mergedStats.naturalCapital,
              newTreesPlanted: metricsData.tree_count ?? mergedStats.newTreesPlanted,
              greenness: metricsData.greenness,
              tempAnomaly: metricsData.temp_anomaly,
              siteArea: metricsData.site_area != null
                ? `${Number(metricsData.site_area).toFixed(2)} acres`
                : mergedStats.siteArea,
            };
          }
        }

        console.log("Fetched stats:", mergedStats);
        if (seq === statsSeqRef.current) {
          setStats(mergedStats);
        }
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      } finally {
        if (seq === statsSeqRef.current) {
          setLoading(false);
        }
      }
    }
  };

  const fetchSiteruns = async (siteCode) => {
    if (!siteCode) return;
    const seq = ++siterunsSeqRef.current;

    setLoadingTimestamps(true);
    try {
      const user = getAuth().currentUser;
      const token = user && await user.getIdToken();

      const response = await fetch(`${BASE_URL}/api/tree-siteruns/${siteCode}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (seq !== siterunsSeqRef.current) return;

      if (response.ok) {
        const data = await response.json();

        if (seq !== siterunsSeqRef.current) return;

        const formattedTimestamps = data
          .filter(item => item.timestamp) // Filter out items without timestamps
          .map(item => {
            const date = new Date(item.timestamp);
            // Check if date is valid
            const isValidDate = !isNaN(date.getTime());
            return {
              label: isValidDate ? date.toLocaleDateString('en-CA') : `Run ${item.run_id?.slice(0, 8) || 'Unknown'}`,
              value: item.run_id
            };
          });

        setAvailableTimestamps(formattedTimestamps);

        if (formattedTimestamps.length > 0) {
          const firstRunId = formattedTimestamps[0].value;
          setSelectedTimestamp(firstRunId);
          setSelectedRunId(firstRunId);
        } else {
          // ✅ If no runs, clear stats and run/timestamp selection
          setSelectedRunId('');
          setSelectedTimestamp('');
          setAvailableTimestamps([]);
          setStats({
            totalTrees: 0,
            canopyCover: '0.00 acres',
            avgTreeHeight: '0.00 m',
            totalWaterBodies: 0,
            totalWaterArea: 0
          });
        }

        console.log(`📅 Fetched timestamps for site ${siteCode}`, formattedTimestamps);
      } else {
        console.error('Failed to fetch timestamps');
        setAvailableTimestamps([]);
      }
    } catch (error) {
      console.error('Error fetching timestamps:', error);
      if (seq === siterunsSeqRef.current) {
        setAvailableTimestamps([]);
      }
    } finally {
      if (seq === siterunsSeqRef.current) {
        setLoadingTimestamps(false);
      }
    }
  };



  const [workOrderStats, setWorkOrderStats] = useState({ active: 0, completed: 0, cancelled: 0 });

  // Fetch work orders (stats + list in one call)
  const fetchWorkOrders = useCallback(async () => {
    if (!selectedOrgId) {
      console.warn("Organisation ID not set yet");
      return;
    }
    setLoadingWorkOrders(true);
    try {
      const user = getAuth().currentUser;
      const token = user && await user.getIdToken();

      const response = await fetch(`${BASE_URL}/api/eco-assets/work-orders?organisation_id=${selectedOrgId}&asset_type=Tree`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWorkOrderStats(data.stats || { active: 0, completed: 0, cancelled: 0 });
        setWorkOrders(Array.isArray(data.work_orders) ? data.work_orders : []);
      } else {
        console.error("Failed to load work orders.");
        setWorkOrders([]);
      }
    } catch (error) {
      console.error("Error loading work orders:", error);
      setWorkOrders([]);
    } finally {
      setLoadingWorkOrders(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedOrgId) {
      fetchWorkOrders();
    }
  }, [selectedOrgId, fetchWorkOrders]);

  // Fetch site data (grids + health counts + site runs in one call)
  const fetchSiteData = useCallback(async () => {
    if (!selectedSiteCode) {
      return;
    }
    
    setGridsLoading(true);
    
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      
      // Use sub-site endpoint if sub-site is selected
      let url;
      if (selectedSubSiteId) {
        url = `${BASE_URL}/api/eco-assets/sub-site-data/${selectedSubSiteId}${selectedRunId ? `?run_id=${selectedRunId}` : ''}`;
      } else {
        url = `${BASE_URL}/api/eco-assets/site-data/${selectedSiteCode}${selectedRunId ? `?run_id=${selectedRunId}` : ''}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update grids
        setGrids(data.grids || []);
        setSelectedGridIds([]);
        
        // Update health counts
        setCounts(data.health_counts || { Risk: 0, Average: 0, Good: 0 });
        
        // Update available timestamps from site runs
        if (data.site_runs && data.site_runs.length > 0) {
          const formattedTimestamps = data.site_runs
            .filter(item => item.run_date || item.timestamp) // Filter out items without dates
            .map(item => {
              const date = new Date(item.run_date || item.timestamp);
              const isValidDate = !isNaN(date.getTime());
              return {
                label: isValidDate ? date.toLocaleDateString('en-CA') : `Run ${item.run_id?.slice(0, 8) || 'Unknown'}`,
                value: item.run_id
              };
            });
          setAvailableTimestamps(formattedTimestamps);
        }
      } else {
        console.error('Site data fetch failed:', response.status);
        setGrids([]);
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
      setGrids([]);
    } finally {
      setGridsLoading(false);
    }
  }, [selectedSiteCode, selectedSubSiteId, selectedRunId]);

  useEffect(() => {
    fetchSiteData();
  }, [fetchSiteData]);

  // V6c: maintenance layer overlay — per-grid tint (green/amber/red/grey)
  useEffect(() => {
    if (viewMode !== 'maintenance' || !selectedSiteCode || !selectedOrgId) return;
    let cancelled = false;
    (async () => {
      try {
        const user = getAuth().currentUser;
        const token = user ? await user.getIdToken() : null;
        const res = await fetch(
          `${BASE_URL}/api/maintenance-map-overlay?organisation_id=${selectedOrgId}&site_id=${selectedSiteCode}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const map = {};
        (data.grids || []).forEach(g => { map[g.grid_id] = g; });
        if (!cancelled) setMaintenanceOverlay(map);
      } catch (err) {
        console.error('Failed to fetch maintenance overlay:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode, selectedSiteCode, selectedOrgId]);

  // Handle grid selection on map
  const handleGridClick = useCallback((gridId, isCtrlPressed) => {
    const grid = grids.find(g => g.id === gridId);
    
    if (isCtrlPressed) {
      setSelectedGridIds(prev => {
        if (prev.includes(gridId)) {
          return prev.filter(id => id !== gridId);
        } else {
          return [...prev, gridId];
        }
      });
      return;
    }
    
    if (grid && grid.has_active_tasks && grid.tasks && grid.tasks.length > 0) {
      const activeTask = grid.tasks.find(t => t.status === 'active');
      if (activeTask) {
        setSelectedGridTaskId(activeTask.id);
        setSelectedGridForTask(gridId);
        setGridTaskModalOpen(true);
        return;
      }
    }
    
    setSelectedGridIds([gridId]);
  }, [grids]);

  handleGridClickRef.current = handleGridClick;

  const handleSelectAllGrids = useCallback(() => {
    setSelectedGridIds(grids.map(g => g.id));
  }, [grids]);

  const handleSelectGridsWithTrees = useCallback(async () => {
    // If no filter is applied, select all grids with trees
    if (selectedFilter === 'All') {
      const gridsWithTrees = grids.filter(g => g.has_trees || g.tree_count > 0);
      setSelectedGridIds(gridsWithTrees.map(g => g.id));
      return;
    }

    // If a filter is applied, fetch grids that have trees matching the filter
    try {
      const token = await getAuth().currentUser.getIdToken();
      const healthValue = healthMap[selectedFilter];
      
      const endpoint = selectedSubSiteId
        ? `${BASE_URL}/api/sub-sites/${selectedSubSiteId}/grids-with-filtered-trees`
        : `${BASE_URL}/api/sites/${selectedSiteCode}/grids-with-filtered-trees`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          health_filter: healthValue,
          run_id: selectedRunId || undefined
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedGridIds(data.grid_ids);
        toast.info(`Selected ${data.count} grids with ${selectedFilter.toLowerCase()} health trees`);
      }
    } catch (error) {
      console.error('Error fetching filtered grids:', error);
      // Fallback to local filtering
      const gridsWithTrees = grids.filter(g => g.has_trees || g.tree_count > 0);
      setSelectedGridIds(gridsWithTrees.map(g => g.id));
    }
  }, [grids, selectedFilter, selectedSiteCode, selectedSubSiteId, selectedRunId]);

  const handleClearGridSelection = useCallback(() => {
    setSelectedGridIds([]);
  }, []);

  // Calculate count of grids with trees (this is an approximation, actual count may differ when filtered)
  const gridsWithTreesCount = grids.filter(g => g.has_trees || g.tree_count > 0).length;

  const handleGridTasksCreated = useCallback((result) => {
    fetchWorkOrders();
    fetchSiteData();
  }, [fetchWorkOrders, fetchSiteData]);

  // Highlight trees on the map (for task tree viewing)
  const handleHighlightTaskTrees = useCallback((trees) => {
    const map = mapRef.current;
    if (!map || !trees || trees.length === 0) return;

    setHighlightedTrees(trees);

    // Create GeoJSON features from tree data
    const features = trees
      .filter(tree => tree.centroid)
      .map(tree => {
        // Handle different centroid formats
        let coordinates;
        if (tree.centroid.coordinates) {
          coordinates = tree.centroid.coordinates;
        } else if (Array.isArray(tree.centroid)) {
          coordinates = tree.centroid;
        } else {
          return null;
        }

        return {
          type: 'Feature',
          properties: {
            id: tree.id,
            health: tree.health,
            species: tree.species,
            water_stress: tree.water_stress
          },
          geometry: {
            type: 'Point',
            coordinates: coordinates
          }
        };
      })
      .filter(Boolean);

    // Check if highlighted-task-trees source exists, if not create it
    if (!map.getSource('highlighted-task-trees')) {
      map.addSource('highlighted-task-trees', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // Add a layer for the highlighted trees
      map.addLayer({
        id: 'highlighted-task-trees-layer',
        type: 'circle',
        source: 'highlighted-task-trees',
        paint: {
          'circle-radius': 10,
          'circle-color': '#6C8EBF',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.9
        }
      });

      // Add pulsing effect layer
      map.addLayer({
        id: 'highlighted-task-trees-pulse',
        type: 'circle',
        source: 'highlighted-task-trees',
        paint: {
          'circle-radius': 16,
          'circle-color': '#6C8EBF',
          'circle-opacity': 0.3
        }
      });
    }

    // Update the source data
    map.getSource('highlighted-task-trees').setData({
      type: 'FeatureCollection',
      features: features
    });

    // Fit map to show all highlighted trees
    if (features.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      features.forEach(f => bounds.extend(f.geometry.coordinates));
      map.fitBounds(bounds, { padding: 100, maxZoom: 18 });
    }
  }, []);

  // Clear highlighted trees from the map
  const handleClearHighlightedTrees = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    setHighlightedTrees([]);

    if (map.getSource('highlighted-task-trees')) {
      map.getSource('highlighted-task-trees').setData({
        type: 'FeatureCollection',
        features: []
      });
    }
  }, []);

  // Handle tree click from task details modal
  const handleTaskTreeClick = useCallback((tree) => {
    const map = mapRef.current;
    if (!map || !tree.centroid) return;

    let coordinates;
    if (tree.centroid.coordinates) {
      coordinates = tree.centroid.coordinates;
    } else if (Array.isArray(tree.centroid)) {
      coordinates = tree.centroid;
    } else {
      return;
    }

    // Fly to the tree location
    map.flyTo({
      center: coordinates,
      zoom: 19,
      essential: true
    });

    // Show popup for the tree
    const healthLabel = tree.health === 'low' ? 'Risk' : 
                       tree.health === 'medium' ? 'Average' : 
                       tree.health === 'high' ? 'Good' : 'N/A';
    
    const healthColor = tree.health === 'low' ? '#D2726A' : 
                       tree.health === 'medium' ? '#E0A458' : 
                       tree.health === 'high' ? '#6BA368' : '#999';

    new mapboxgl.Popup({ closeOnClick: true })
      .setLngLat(coordinates)
      .setHTML(`
        <div style="font-family: 'Public Sans', sans-serif; padding: 8px;">
          <h4 style="margin: 0 0 8px 0; font-size: 14px;">🌳 Tree ${tree.id}</h4>
          <p style="margin: 4px 0; font-size: 12px;">
            <strong>Health:</strong> 
            <span style="color: ${healthColor}; font-weight: 600;">${healthLabel}</span>
          </p>
          ${tree.species ? `<p style="margin: 4px 0; font-size: 12px;"><strong>Species:</strong> ${tree.species}</p>` : ''}
          ${tree.water_stress ? `<p style="margin: 4px 0; font-size: 12px;"><strong>Water Stress:</strong> ${tree.water_stress}</p>` : ''}
        </div>
      `)
      .addTo(map);
  }, []);

  // Close grid task details modal
  const handleCloseGridTaskModal = useCallback(() => {
    setGridTaskModalOpen(false);
    setSelectedGridTaskId(null);
    setSelectedGridForTask(null);
    handleClearHighlightedTrees();
  }, [handleClearHighlightedTrees]);

  // Map initialization (only once)
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: {
          version: 8,
          sources: {
            'custom-tileset': {
              type: 'raster',
              url: 'mapbox://pranavmyname.coimb',
              tileSize: 256
            },
            'siruthuli-crpf': {
              type: 'raster',
              url: 'mapbox://pranavmyname.6p289rx9',
              tileSize: 256
            },
            'siruthuli-railway': {
              type: 'raster',
              url: 'mapbox://pranavmyname.siruthuli-orthomosaic-clipped',
              tileSize: 256
            },
            'google-satellite': {
              type: 'raster',
              tiles: ['https://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}'],
              tileSize: 256,
              attribution: 'Google'
            }
          },
          layers: [
            {
              id: 'google-satellite-layer',
              type: 'raster',
              source: 'google-satellite',
              minzoom: 0,
              maxzoom: 22
            },
            {
              id: 'custom-tileset-layer',
              type: 'raster',
              source: 'custom-tileset',
              minzoom: 0,
              maxzoom: 22
            },
            {
              id: 'siruthuli-crpf-layer',
              type: 'raster',
              source: 'siruthuli-crpf',
              minzoom: 0,
              maxzoom: 22
            },
            {
              id: 'siruthuli-railway-layer',
              type: 'raster',
              source: 'siruthuli-railway',
              minzoom: 0,
              maxzoom: 22
            }
          ]
        },
        center: mapCenter,
        zoom: 17,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-left");
    }

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Modern color palette (Material Design)
  const colors = {
    primary: '#6C8EBF',
    primaryDark: '#47638F',
    success: '#6BA368',
    warning: '#E0A458',
    error: '#D2726A',
    info: '#86A9C4',
    infoDark: '#5E86A3',
    white: '#ffffff',
    grey: '#9e9e9e',
    greyLight: '#e0e0e0',
  };

  const blue = colors.primary;
  const white = colors.white;
  const gray = colors.grey;


  // 🔲 Grid layer rendering - optimized for proper layer ordering
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    console.log('🔲 Grid layer effect:', { viewMode, gridsCount: grids.length, mapLoaded: !!map });

    const renderGridLayers = () => {
      // Clean up grid layers first
      ['grid-outlines', 'grid-fills'].forEach(layerId => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });
      if (map.getSource('grids')) map.removeSource('grids');

      // Only render grids in grid/maintenance mode with valid data
      if ((viewMode !== 'grids' && viewMode !== 'maintenance') || grids.length === 0) {
        console.log('🔲 Skipping grid render:', { viewMode, gridsCount: grids.length });
        return;
      }

      console.log('🔲 Rendering', grids.length, 'grids on map');

      // Build features with numeric IDs for feature-state (Mapbox requires numeric IDs)
      const gridFeatures = grids.map((grid, index) => ({
        type: "Feature",
        id: index + 1, // Numeric ID for Mapbox feature-state
        geometry: grid.geometry,
        properties: {
          grid_id: grid.id, // Original string ID in properties
          grid_index: grid.grid_index,
          task_count: grid.task_count || 0,
          has_active_tasks: grid.has_active_tasks || false,
          area_m2: grid.area_m2,
          maintenance_tint: maintenanceOverlay[grid.id]?.tint || 'grey', // V6c
        }
      }));

      // Add grid source
      map.addSource("grids", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: gridFeatures
        }
      });

      // Add grid fill layer - ensure it's on top by not specifying beforeId
      // This will add it on top of all existing layers
      map.addLayer({
        id: "grid-fills",
        type: "fill",
        source: "grids",
        paint: {
          "fill-color": viewMode === 'maintenance' ? [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            "#3b82f6", // Blue when selected
            ["==", ["get", "maintenance_tint"], "red"],
            "#D2726A",
            ["==", ["get", "maintenance_tint"], "amber"],
            "#E0A458",
            ["==", ["get", "maintenance_tint"], "green"],
            "#6BA368",
            "#9e9e9e" // grey — no plan covers this grid
          ] : [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            "#3b82f6", // Blue when selected
            ["boolean", ["get", "has_active_tasks"], false],
            "#fbbf24", // Yellow with active tasks
            "#10b981" // Green when no tasks
          ],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            0.6,
            ["boolean", ["feature-state", "hover"], false],
            0.5,
            0.35
          ]
        }
      });

      // Add grid outline layer on top of fills
      map.addLayer({
        id: "grid-outlines",
        type: "line",
        source: "grids",
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            "#2563eb",
            "#ffffff"
          ],
          "line-width": [
            "case",
            ["boolean", ["feature-state", "selected"], false],
            3,
            2
          ]
        }
      });

      // Grid labels removed - they require glyphs in map style which causes errors
      // Grid info is visible via tooltips on hover instead

      // Grid click handler - use grid_id from properties
      const onGridClick = (e) => {
        if (e.features.length === 0) return;
        const gridId = e.features[0].properties.grid_id;

        if (viewMode === 'maintenance') {
          const overlay = maintenanceOverlay[gridId];
          const html = `
            <div style="font-size:12px; min-width:180px;">
              ${overlay
                ? `<p style="margin:2px 0;"><strong>${overlay.plan_name || 'Maintenance'}</strong></p>
                   <p style="margin:2px 0;">Last visit: ${overlay.last_visit_outcome || '—'}${overlay.last_visit_date ? ` (${new Date(overlay.last_visit_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})` : ''}</p>
                   <p style="margin:2px 0;">Next due: ${overlay.next_due ? new Date(overlay.next_due).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</p>`
                : '<p style="margin:2px 0;">No maintenance plan covers this grid.</p>'
              }
            </div>
          `;
          const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
            .setLngLat(e.lngLat)
            .setHTML(html)
            .addTo(map);
          const container = popup.getElement();
          const btn = document.createElement('button');
          btn.textContent = 'View tasks →';
          btn.style.cssText = 'margin-top:4px;font-size:11px;padding:2px 8px;cursor:pointer;';
          btn.onclick = () => navigate(`/maintenance?site=${selectedSiteCode}`);
          container.querySelector('.mapboxgl-popup-content')?.appendChild(btn);
          return;
        }

        const isCtrlPressed = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
        handleGridClickRef.current?.(gridId, isCtrlPressed);
      };

      // Grid hover handlers
      let hoveredGridId = null;
      
      const onGridMouseEnter = (e) => {
        map.getCanvas().style.cursor = "pointer";
        if (e.features.length > 0) {
          if (hoveredGridId !== null) {
            map.setFeatureState({ source: "grids", id: hoveredGridId }, { hover: false });
          }
          hoveredGridId = e.features[0].id;
          map.setFeatureState({ source: "grids", id: hoveredGridId }, { hover: true });
        }
      };

      const onGridMouseLeave = () => {
        map.getCanvas().style.cursor = "";
        if (hoveredGridId !== null) {
          map.setFeatureState({ source: "grids", id: hoveredGridId }, { hover: false });
          hoveredGridId = null;
        }
      };

      map.on("click", "grid-fills", onGridClick);
      map.on("mouseenter", "grid-fills", onGridMouseEnter);
      map.on("mouseleave", "grid-fills", onGridMouseLeave);

      // Return cleanup for event listeners inside renderGridLayers
      return () => {
        map.off("click", "grid-fills", onGridClick);
        map.off("mouseenter", "grid-fills", onGridMouseEnter);
        map.off("mouseleave", "grid-fills", onGridMouseLeave);
      };
    }; // End of renderGridLayers

    let removeListeners = null;
    let cancelled = false;

    const setupGridLayers = () => {
      if (cancelled) return;
      if (removeListeners) removeListeners();
      removeListeners = renderGridLayers() || null;
    };

    if (map.isStyleLoaded()) {
      setupGridLayers();
    } else {
      map.once('idle', setupGridLayers);
    }

    return () => {
      if (!mapRef.current) return; // map already removed on unmount
      cancelled = true;
      map.off('idle', setupGridLayers);
      if (removeListeners) removeListeners();
      if (viewMode !== 'grids' && viewMode !== 'maintenance') {
        ['grid-outlines', 'grid-fills'].forEach(layerId => {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        });
        if (map.getSource('grids')) map.removeSource('grids');
      }
    };
  }, [viewMode, grids, maintenanceOverlay, selectedSiteCode]);

  // Keep grid layers above tree polygons while in grid selection/maintenance mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map || (viewMode !== 'grids' && viewMode !== 'maintenance')) return;
    if (!map.getLayer('grid-fills')) return;
    map.moveLayer('grid-fills');
    if (map.getLayer('grid-outlines')) {
      map.moveLayer('grid-outlines');
    }
  }, [viewMode, selectedFilter, selectedRunId, selectedSiteCode, selectedSubSiteId, grids.length]);

  // 🔲 Update grid selection states
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource("grids")) return;

    // Create a mapping from string ID to numeric feature ID
    const gridIdToNumericId = {};
    grids.forEach((grid, index) => {
      gridIdToNumericId[grid.id] = index + 1;
    });

    // Clear all selections (use numeric IDs)
    grids.forEach((grid, index) => {
      map.setFeatureState({ source: "grids", id: index + 1 }, { selected: false });
    });

    // Apply new selections (convert string ID to numeric ID)
    selectedGridIds.forEach(gridId => {
      const numericId = gridIdToNumericId[gridId];
      if (numericId) {
        map.setFeatureState({ source: "grids", id: numericId }, { selected: true });
      }
    });
  }, [selectedGridIds, grids]);

  // 🔲 Note: Trees remain visible in both modes - user requested to see both polygons and grids

  useEffect(() => {
    if (mapRef.current && mapRef.current.getLayer("tree-layer")) {
      mapRef.current.setPaintProperty("tree-layer", "fill-opacity", polygonOpacity);
    }
  }, [polygonOpacity]);


  useEffect(() => {
    if (!mapRef.current || !selectedSiteCode) return;

    // ✅ Clear previous highlight and opacity override when filters/site change
    clearHighlight();

    const map = mapRef.current;
    let teardown = null;

    const setup = () => {

    const healthValue = selectedFilter === "All" ? "" : healthMap[selectedFilter];

    // Remove old source/layer if they exist
    if (map.getLayer("tree-layer")) {
      map.removeLayer("tree-layer");
    }
    if (map.getSource("trees")) {
      map.removeSource("trees");
    }

    let tileURL = selectedSubSiteId
      ? `${BASE_URL}/mvt/trees/{z}/{x}/{y}.pbf?sub_site_id=${selectedSubSiteId}`
      : `${BASE_URL}/mvt/trees/{z}/{x}/{y}.pbf?site_id=${selectedSiteCode}`;

    if (selectedRunId) {
      tileURL += `&run_id=${selectedRunId}`;
    }

    if (healthValue) {
      tileURL += `&health=${healthValue}`;
    }

    tileURL += `&_t=${Date.now()}`; // Cache busting

    console.log(`🗺️ Adding Eco MVT source: ${tileURL}`);

    map.addSource("trees", {
      type: "vector",
      tiles: [tileURL],
      minzoom: 0,
      maxzoom: 22,
    });

    // Modern color palette (Material Design)
    const white = '#ffffff';
    const highColor = '#6BA368';  // success green
    const medColor = '#E0A458';   // warning amber
    const lowColor = '#D2726A';   // error red
    const aqua = '#86A9C4';       // info cyan
    const titleColor = '#6C8EBF'; // primary blue

    // ✅ Add the tree layer with dynamic colors
    map.addLayer({
      id: "tree-layer",
      type: "fill",
      source: "trees",
      "source-layer": "trees",
      paint: {
        "fill-color": [
          "match",
          ["get", "health"],
          "high", highColor,
          "medium", medColor,
          "low", lowColor,
          white
        ],
        "fill-opacity": polygonOpacity
        ,
        "fill-outline-color": white
      }
    });

    // Add highlight source/layer only once
    if (!map.getSource("highlighted-tree")) {
      map.addSource("highlighted-tree", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      map.addLayer({
        id: "highlighted-tree-layer",
        type: "fill",
        source: "highlighted-tree",
        paint: {
          "fill-color": aqua,
          "fill-opacity": 0.8,
          "fill-outline-color": white
        }
      });
    }

    // 👉 Declare popup outside to track it globally
    let currentPopup = null;

    const handleMapClick = (e) => {
  if (viewModeRef.current === 'grids') return;

  // Close previous popup if exists
  if (currentPopup) {
    currentPopup.remove();
    currentPopup = null;
  }

  const feature = e.features[0];
  if (!feature) return;

  const { id, species, health, height_m, area } = feature.properties;

  // ✅ Create popup container
  const popupContent = document.createElement('div');
  popupContent.style.fontFamily = 'Arial, sans-serif';
  popupContent.style.padding = '8px';
  popupContent.style.maxWidth = '220px';
  popupContent.style.position = 'relative';

  const closeButton = document.createElement('span');
  closeButton.textContent = '✕';
  closeButton.style.position = 'absolute';
  closeButton.style.top = '9px';
  closeButton.style.right = '8px';
  closeButton.style.cursor = 'pointer';
  closeButton.style.fontSize = '12px';
  closeButton.style.color = lowColor;

  // ✅ Build popup dynamically
  let html = `
    <h3 style="margin: 0; color:${titleColor}; font-size: 12px;">Tree Details</h3>
    <hr style="margin: 4px 0;">
    <p style="margin: 4px 0; font-size: 10px;"><strong>ID:</strong> ${id}</p>
  `;

  if (health) {
    html += `<p style="margin: 4px 0; font-size: 10px;"><strong>Health:</strong> ${health}</p>`;
  }

  if (height_m && height_m !== 'null' && height_m !== 'undefined') {
    html += `<p style="margin: 4px 0; font-size: 10px;"><strong>Height:</strong> ${height_m} m</p>`;
  }

  if (area) {
    html += `<p style="margin: 4px 0; font-size: 10px;"><strong>Area:</strong> ${area} sq.m</p>`;
  }

  popupContent.innerHTML = html;
  popupContent.appendChild(closeButton);

  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  })
    .setLngLat(e.lngLat)
    .setDOMContent(popupContent)
    .addTo(map);

  currentPopup = popup;

  closeButton.onclick = () => {
    popup.remove();
    currentPopup = null;
  };
};

    map.on('click', 'tree-layer', handleMapClick);

    // 👉 Close popup when clicking anywhere else on the map
    const handleMapOutsideClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['tree-layer'] });
      if (features.length === 0 && currentPopup) {
        currentPopup.remove();
        currentPopup = null;
      }
    };

    map.on('click', handleMapOutsideClick);

    // 👉 Change cursor to pointer when hovering trees (not in grid selection mode)
    map.on('mouseenter', 'tree-layer', () => {
      if (viewModeRef.current === 'grids') return;
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'tree-layer', () => {
      if (viewModeRef.current === 'grids') return;
      map.getCanvas().style.cursor = '';
    });

    // 👉 Cleanup
    teardown = () => {
      map.off('click', 'tree-layer', handleMapClick);
      map.off('click', handleMapOutsideClick);
      if (currentPopup) {
        currentPopup.remove();
        currentPopup = null;
      }
    };
    }; // end setup

    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('idle', setup);
    }

    return () => {
      if (!mapRef.current) return; // map already removed on unmount
      map.off('idle', setup);
      if (teardown) teardown();
    };
  }, [selectedSiteCode, selectedSubSiteId, selectedFilter, selectedRunId]);

 
  const handleLocateTree = (lat, lng, treeId) => {
  if (!mapRef.current) return;
  const map = mapRef.current;

  // 🟢 Center and zoom to the tree
  map.easeTo({
    center: [lng, lat],
    zoom: 19,
    offset: [0, -window.innerHeight / 5],
    duration: 1500,
    essential: true
  });

  const features = map.querySourceFeatures('trees', { sourceLayer: 'trees' });
  const matched = features.find(f => f.properties.id === treeId);

  if (matched) {
    const geojsonFeature = {
      type: 'Feature',
      geometry: matched.geometry,
      properties: matched.properties
    };

    // 🟡 Highlight selected tree
    map.getSource('highlighted-tree')?.setData({
      type: 'FeatureCollection',
      features: [geojsonFeature]
    });

    // 🟡 Dim other polygons temporarily
    map.setPaintProperty('tree-layer', 'fill-opacity', [
      'case',
      ['==', ['get', 'id'], treeId],
      0, // make selected transparent
      polygonOpacity
    ]);

    // 🕒 Automatically clear highlight after 3 seconds
    setTimeout(() => {
  map.setPaintProperty('tree-layer', 'fill-opacity-transition', { duration: 1000 });
  map.setPaintProperty('tree-layer', 'fill-opacity', polygonOpacity);
  map.getSource('highlighted-tree')?.setData({
    type: 'FeatureCollection',
    features: []
  });
}, 3000);

  }
};



  const clearHighlight = () => {
    const map = mapRef.current;
    if (!map) return;

    map.setPaintProperty('tree-layer', 'fill-opacity', polygonOpacity); // back to normal
    map.getSource('highlighted-tree')?.setData({
      type: 'FeatureCollection',
      features: []
    });
  };





  const fetchSites = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      const idToken = await user.getIdToken();

      try {
        const response = await axios.get(`${BASE_URL}/api/selectionsites?organisation_id=${selectedOrgId}`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        const rawSites = response.data;

        // Convert WKT to GeoJSON
        const polygons = rawSites.map(site => {
          const geojsonGeom = wellknown(site.polygon); // WKT → GeoJSON geometry
          return {
            type: "Feature",
            geometry: geojsonGeom,
            properties: {
              site_id: site.site_id,
              name: site.site_name,
              site_number: site.site_number,
            }
          };
        });

        setSites(rawSites);           // Still keep original

      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    }
  };

  useEffect(() => {
    if (selectedOrgId) {
      setSelectedSite("");
      setSelectedSiteCode("");
      setSubSites([]);
      setSelectedSubSiteId("");
      setSelectedSubSiteName("");
      setSelectedRunId('');
      setSelectedTimestamp('');
      setAvailableTimestamps([]);
      setGrids([]);
      fetchSites();
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (sites.length > 0 && !selectedSite) {
      // E4: don't auto-select the default site when a ?site= deep-link is present;
      // let the deep-link effect below handle selection instead.
      const params = new URLSearchParams(window.location.search);
      const siteParam = params.get('site');
      if (siteParam && sites.some(s => s.site_id === siteParam)) {
        return;
      }
      // Try to find "ENCO-ECOL-EM7"
      const defaultSite = sites.find(site => site.site_id === "MECC-01-TM-1");

      if (defaultSite) {
        setSelectedSite(defaultSite.site_name);
        handleSiteChange({ target: { value: defaultSite.site_name } });
      } else {
        // Fallback to first site with "TM"
        const emSites = sites.filter(site => site.site_id.includes("TM"));
        if (emSites.length > 0) {
          setSelectedSite(emSites[0].site_name);
          handleSiteChange({ target: { value: emSites[0].site_name } });
        }
      }
    }
  }, [sites]);

  // Deep-link support: preselect site (and grid) from ?site=&grid= URL params, once only.
  const appliedSiteParamRef = useRef(false);
  useEffect(() => {
    if (appliedSiteParamRef.current) return;
    if (sites.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const siteParam = params.get('site');
    if (!siteParam) return;
    const match = sites.find(s => s.site_id === siteParam);
    if (match) {
      appliedSiteParamRef.current = true;
      setSelectedSite(match.site_name);
      handleSiteChange({ target: { value: match.site_name } });
    }
  }, [sites]);

  const appliedGridParamRef = useRef(false);
  useEffect(() => {
    if (appliedGridParamRef.current) return;
    if (grids.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const gridParam = params.get('grid');
    if (!gridParam) return;
    const match = grids.find(g => String(g.id) === gridParam);
    if (match) {
      appliedGridParamRef.current = true;
      setSelectedGridIds([match.id]);
    }
  }, [grids]);

  useEffect(() => {
    if (selectedSiteCode) {
      // Reset selected timestamp/run when site changes
      setSelectedTimestamp('');
      setSelectedRunId('');
      fetchSiteruns(selectedSiteCode);
    }
  }, [selectedSiteCode]);


  useEffect(() => {
    // ✅ If run_id is cleared, still fetch stats (it should return all)
    if (selectedSiteCode) {
      console.log("Run ID changed, fetching stats for:", selectedRunId);
      fetchStats();
    }
  }, [selectedRunId]);


  // Fetch sub-sites when site changes
  const fetchSubSites = async (siteCode) => {
    const seq = ++subSitesSeqRef.current;
    if (!siteCode) {
      setSubSites([]);
      setSelectedSubSiteId("");
      setSelectedSubSiteName("");
      return;
    }
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${BASE_URL}/api/sites/${siteCode}/sub-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (seq !== subSitesSeqRef.current) return;
      if (response.ok) {
        const data = await response.json();
        if (seq !== subSitesSeqRef.current) return;
        setSubSites(data);
        // Default to "All Plots" (empty string) so aggregated stats show first
        setSelectedSubSiteId("");
        setSelectedSubSiteName("");
      } else {
        setSubSites([]);
        setSelectedSubSiteId("");
        setSelectedSubSiteName("");
      }
    } catch (err) {
      console.error('Error fetching sub-sites:', err);
      if (seq === subSitesSeqRef.current) {
        setSubSites([]);
      }
    }
  };

  const handleSubSiteChange = (e) => {
    const subSiteId = e.target.value;
    setSelectedSubSiteId(subSiteId);
    const sub = subSites.find(s => s.sub_site_id === subSiteId);
    // Empty value = "All Plots" aggregate view
    setSelectedSubSiteName(subSiteId ? (sub ? (sub.name || sub.sub_site_id) : "") : "");
    // Do NOT reset run/timestamps — runs are site-level, remain valid across sub-site changes
  };

  // Render sub-site boundary overlays on the Mapbox map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const SOURCE_ID = 'sub-sites-overlay';
    const FILL_LAYER = 'sub-sites-fill';
    const LINE_LAYER = 'sub-sites-line';
    const LABEL_LAYER = 'sub-sites-label';

    const setup = () => {
      // Clean up existing layers/source
      [LABEL_LAYER, FILL_LAYER, LINE_LAYER].forEach(id => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);

      // Determine which sub-sites to display
      const toDisplay = selectedSubSiteId
        ? subSites.filter(s => s.sub_site_id === selectedSubSiteId)
        : subSites;

      if (!toDisplay.length) return;

      const features = toDisplay
        .filter(ss => ss.geometry)
        .map(ss => ({
          type: 'Feature',
          geometry: ss.geometry,
          properties: { name: ss.name || ss.sub_site_id, sub_site_id: ss.sub_site_id }
        }));

      if (!features.length) return;

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features }
      });

      map.addLayer({
        id: FILL_LAYER,
        type: 'fill',
        source: SOURCE_ID,
        paint: {
          'fill-color': '#E0A458',
          'fill-opacity': 0.12
        }
      });

      map.addLayer({
        id: LINE_LAYER,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': '#E0A458',
          'line-width': 2,
          'line-dasharray': [4, 2]
        }
      });

      map.addLayer({
        id: LABEL_LAYER,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 12,
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#e65100',
          'text-halo-color': '#fff',
          'text-halo-width': 1.5
        }
      });
    };

    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('idle', setup);
    }

    return () => {
      if (!mapRef.current) return; // map already removed on unmount
      map.off('idle', setup);
      [LABEL_LAYER, FILL_LAYER, LINE_LAYER].forEach(id => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  }, [subSites, selectedSubSiteId]);

  // Handle site selection change
  const handleSiteChange = (e) => {
    const siteName = e.target.value;
    setSelectedSite(siteName);
    // Reset sub-site
    setSelectedSubSiteId("");
    setSelectedSubSiteName("");
    setSubSites([]);

    const selectedSiteObj = sites.find(site => site.site_name === siteName);

    if (selectedSiteObj) {
      console.log("Selected Site:", selectedSiteObj);
      // Update site code
      const newSiteCode = selectedSiteObj.site_id.toString();
      setSelectedSiteCode(newSiteCode);
      fetchSubSites(newSiteCode);


      if (selectedSiteObj.polygon) {
        console.log("Raw WKT Polygon:", selectedSiteObj.polygon);

        const geoJson = wellknown.parse(selectedSiteObj.polygon);
        console.log("Parsed GeoJSON:", geoJson);

        const coords = geoJson?.coordinates?.[0]?.[0];
        if (coords && coords.length >= 3) {
          const center = getPolygonCenter(coords);
          if (!isNaN(center[0]) && !isNaN(center[1])) {
            console.log("Polygon Center:", center);
            setMapCenter(center);
          } else {
            console.warn("Invalid center coordinates:", center);
          }
        } else {
          console.warn("Invalid or missing polygon coordinates:", geoJson);
        }
      } else {
        console.error("Polygon data is missing for the selected site.");
      }

    } else {
      console.log("Site not found!");
      setSelectedSiteCode("");
      setSelectedSiteDetails(null); // Reset site details
    }
  };




  // Function to calculate centroid of a polygon
  const getPolygonCenter = (coordinates) => {
    let sumLat = 0, sumLng = 0, count = coordinates.length;
    coordinates.forEach(coord => {
      sumLng += coord[0];
      sumLat += coord[1];
    });
    return [sumLng / count, sumLat / count]; // [lng, lat]
  };

  useEffect(() => {
    if (mapRef.current && mapCenter) {
      console.log("Moving map to:", mapCenter);
      mapRef.current.flyTo({
        center: mapCenter,
        zoom: 16.5,
        duration: 1000,
        essential: true
      });
    }
  }, [mapCenter]);






  // Render Work Orders
  const renderWorkOrders = () => {
    if (!Array.isArray(workOrders)) return null;

    return workOrders.map(order => {
      const stage = order.currentStage;
      const completed = order.stagesCompleted ?? 0;
      const total = order.stagesTotal ?? 0;
      const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;
      return (
        <div
          className="work-order-card"
          key={order.id}
          onClick={(e) => handleRowClick(order, e, "workorder")}
          style={{ cursor: "pointer" }}
        >
          <div className="work-order-header">
            <div className="status">
              <span>Status</span>
              <span className={`Status-badge ${order.status.toLowerCase()}`}>{order.status}</span>
            </div>
            {order.workflowName && (
              <span style={{ fontSize: '0.72rem', color: '#6b7280', marginLeft: 'auto' }}>
                {order.workflowName}
              </span>
            )}
          </div>
          {stage && (
            <div className="detail-row" style={{ marginBottom: '4px' }}>
              <span className="detail-label">Current Stage</span>
              <span style={{ fontWeight: 500, color: '#2563eb' }}>: {stage.name}</span>
            </div>
          )}
          {total > 0 && (
            <div style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.70rem', color: '#6b7280', marginBottom: '2px' }}>
                <span>Progress</span>
                <span>{completed}/{total} stages</span>
              </div>
              <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progressPct}%`, background: progressPct === 100 ? '#10b981' : '#3b82f6', borderRadius: '2px', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
          <div className="work-order-details">
            <div className="detail-row">
              <span className="detail-label">Title</span>
              <span>: {order.title || order.description || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Assigned To</span>
              <span>: {order.assignedUserName || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date</span>
              <span>: {formatDate(order.createdAt)}</span>
            </div>
          </div>
        </div>
      );
    });
  };


  // Function to format date as YYYY-MM-DD HH:MM (24-hour format, no seconds)
  // Function to format date as YYYY-MM-DD HH:MM (24-hour format, no seconds, with dashes)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true // Enables AM/PM format
    })
      .replace(",", "")   // Remove the comma between date and time
      .replace(/\//g, "-"); // Replace slashes with dashes
    // .replace(":", "-");  // Replace colon in time with a dash
  };


  const onDataClick = () => {
    console.log("Data button clicked");
    setShowDataModal(true);
  };


  // ✅ Listen to LayoutWrapper's sidebar state (the REAL sidebar)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === 'true'; // LayoutWrapper stores as string
  });

  // ✅ Listen for sidebar toggle events from LayoutWrapper
  useEffect(() => {
    const handleSidebarToggle = (event) => {
      setIsCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);

    return () => {
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
    };
  }, []);

  // Remove the toggleSidebar function as it's controlled by LayoutWrapper


  const handleCloseTreeList = () => {
    const map = mapRef.current;
    if (!map) return;

    // 🟢 Reset the main tree layer filter to show all trees
    if (map.getLayer('tree-layer')) {
      map.setFilter('tree-layer', null);
    }

    // 🟡 Clear the highlight layer
    if (map.getSource('highlighted-tree')) {
      map.getSource('highlighted-tree').setData({
        type: 'FeatureCollection',
        features: []
      });
    }

    clearHighlight();
    // 🔴 Hide the TreeList modal
    setShowDataModal(false);
    // 🔴 Clear tree selection when closing
    setSelectedTreeIds([]);
    setSelectedTreesData([]);
  };

  // 🌳 Handle tree selection changes from TreeList
  const handleTreeSelectionChange = useCallback((treeIds, treesData) => {
    setSelectedTreeIds(treeIds);
    setSelectedTreesData(treesData);
  }, []);

  return (
    <Box sx={{ 
      height: '100%',
      width: '100%',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="public-sans" style={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* Main Content */}
          <main style={{ width: '100%', height: '100%', position: 'relative' }}>

            {/* Map Container - Fills entire background */}
            <div 
              id="map" 
              ref={mapContainerRef} 
              className="map-container"
            ></div>
            
            {/* TreeList Data Browser - Positioned at bottom between left and right panels */}
            {showDataModal && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: '20px',
                  // Calculate left position based on main sidebar state (isCollapsed)
                  // MainContent left position + panel width + gaps
                  // Sidebar expanded (260px): 260 (MainContent left) + 16 (gap) + 320 (panel) + 22 (gap) = 618px
                  // Sidebar collapsed (70px): 70 (MainContent left) + 16 (gap) + 320 (panel) + 22 (gap) = 428px
                  left: isCollapsed ? '428px' : '618px',
                  
                  // Right position: MainContent has fixed right=0, so calculate from screen right
                  // Work order panel: 16 (from right) + 320 (panel width) + 22 (gap) = 358px
                  // Work order minimized: 16 + 48 + 22 = 86px
                  right: '358px',
                  
                  height: '35vh',
                  maxHeight: '400px',
                  minWidth: '500px',
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)',
                  zIndex: 100,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'left 0.3s ease, right 0.3s ease, width 0.3s ease',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <TreeList
                  key={isCollapsed ? 'collapsed' : 'expanded'}
                  onClose={handleCloseTreeList}
                  selectedSiteCode={selectedSiteCode}
                  selectedSubSiteId={selectedSubSiteId}
                  selectedRunId={selectedRunId}
                  selectedHealthFilter={selectedFilter}
                  onLocateTree={handleLocateTree}
                  onTreeSelectionChange={handleTreeSelectionChange}
                />
              </Box>
            )}
            {/* ✅ Expandable Floating Panels - Fixed: Added z-index */}
            <div 
              className={`SiteBarParentSection ${isCollapsed ? 'expand' : 'shrink'} ${isSiteDetailsPanelMinimized ? 'minimized' : ''}`}
              style={{ zIndex: 'var(--z-panels)' }}
            >
              <div className="resizable-div SiteBarSubSection">
                {isSiteDetailsPanelMinimized ? (
                  <Tooltip title="Show Site Details" placement="right" arrow>
                    <Box 
                      className="minimized-icon-btn"
                      onClick={() => setIsSiteDetailsPanelMinimized(false)}
                    >
                      <MapPin size={22} color="#47638F" />
                    </Box>
                  </Tooltip>
                ) : (
                  <>
                    {/* Full Panel Content */}
                    <Box sx={getSiteSelectionDivSx(theme)}>
                      <Box sx={getLabelMinimizeDivSx(theme)}>
                        <Box component="span" sx={getLabelTextSx(theme)}>Site Details</Box>
                        <Box component="span" sx={getLabelIconSx(theme)} onClick={() => setIsSiteDetailsPanelMinimized((prev) => !prev)}>
                          <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                            <title>Minimize</title>
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M20 6v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2z"></path>
                            <path d="M20 15h-16"></path>
                            <path d="M14 10l-2 -2l-2 2"></path>
                          </svg>
                        </Box>
                      </Box>
                      <div className="SiteSelectionSelectDiv">
                        <Box sx={{ minWidth: 170 }}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel
                              id="site-select-label"
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                top: '-8px', // same as Timestamp
                                '&.MuiInputLabel-shrink': {
                                  top: '0px', // floating position
                                },
                              }}>Site</InputLabel>
                            <Select
                              labelId="site-select-label"
                              id="site-select"
                              value={selectedSite}
                              label="Site"
                              onChange={handleSiteChange}
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                height: '35px',
                                borderRadius: '8px',
                                backgroundColor: white,
                                boxShadow: 'none',
                                '.MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'border',
                                  borderWidth: '1px',
                                  borderStyle: 'solid'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gray },
                                '.MuiSelect-select': {
                                  paddingTop: '10px',
                                  paddingBottom: '10px',
                                },
                              }}
                            >
                              <MenuItem value="">Select a Site</MenuItem>
                              {sites
                                .filter((site) => site.site_id.includes("TM")) // filter same as before
                                .map((site) => (
                                  <MenuItem key={site.site_id} value={site.site_name}>
                                    {site.site_name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Box>
                        {/* <button className="ShowBtnStyle" onClick={() => console.log("Selected Site:", selectedSite, "Code:", selectedSiteCode)}>
                          Show
                        </button> */}

                        <header>
                          <Button
                            variant="contained"
                            className="ShowBtnStyle"
                            onClick={() => console.log("Selected Site:", selectedSite, "Code:", selectedSiteCode)}
                            sx={{
                              backgroundColor: blue,  // example blue
                              color: white,
                              fontSize: "12px",            // 👈 text size here
                              padding: "6px 16px",
                              borderRadius: "6px",
                              textTransform: "none", // prevent ALL CAPS
                              height: "30px",
                              width: "70px",
                              lineHeight: "1px",
                              fontFamily: "'Public Sans', sans-serif",
                              "&:hover": {
                                backgroundColor: blue,
                              }
                            }}
                          >
                            Show
                          </Button>
                        </header>

                      </div>




                      {/* Sub-site selector */}
                      {subSites.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel
                              id="sub-site-select-label"
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                top: '-8px',
                                '&.MuiInputLabel-shrink': { top: '0px' },
                              }}>Plot</InputLabel>
                            <Select
                              labelId="sub-site-select-label"
                              id="sub-site-select"
                              value={selectedSubSiteId}
                              label="Plot"
                              onChange={handleSubSiteChange}
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                height: '35px',
                                borderRadius: '8px',
                                backgroundColor: white,
                                '.MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'border',
                                  borderWidth: '1px',
                                  borderStyle: 'solid'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gray },
                                '.MuiSelect-select': { paddingTop: '10px', paddingBottom: '10px' },
                              }}
                            >
                              <MenuItem value="">All Plots</MenuItem>
                              {subSites.map((sub) => (
                                <MenuItem key={sub.sub_site_id} value={sub.sub_site_id}>
                                  {sub.name || sub.sub_site_id}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}

                      {/* Display selected site & code */}
                      {selectedSite && (
                        <Box sx={getSiteSelectionDivSx(theme)}>
                          <Box component="span" sx={getLabelTextSx(theme)}>{selectedSite}</Box>
                          <div>
                            <span className="SiteCodeDiv">Site Code : {selectedSiteCode ? selectedSiteCode : "N/A"}</span>
                            {selectedSubSiteId && (
                              <span className="SiteCodeDiv" style={{ marginTop: 2, display: 'block' }}>
                                Plot: {selectedSubSiteName || selectedSubSiteId}
                              </span>
                            )}
                          </div>
                        </Box>
                      )}

                      {/* V7 Site Operations card (Feature 4) */}
                      {selectedSiteCode && <SiteOpsCard siteId={selectedSiteCode} />}
                    </Box>



                    <div className="filter-buttons">
                      <button
                        onClick={() => handleFilterClick('All')}
                        className={`filter-btn1 all ${selectedFilter === 'All' ? 'active' : ''}`}
                      >
                        All ({counts.All || (counts.Risk + counts.Average + counts.Good)})
                      </button>
                      <button
                        onClick={() => handleFilterClick('Risk')}
                        className={`filter-btn1 risk ${selectedFilter === 'Risk' ? 'active' : ''}`}
                      >
                        Risk ({counts.Risk})
                      </button>
                      <button
                        onClick={() => handleFilterClick('Average')}
                        className={`filter-btn1 average ${selectedFilter === 'Average' ? 'active' : ''}`}
                      >
                        Average ({counts.Average})
                      </button>
                      <button
                        onClick={() => handleFilterClick('Good')}
                        className={`filter-btn1 good ${selectedFilter === 'Good' ? 'active' : ''}`}
                      >
                        Good ({counts.Good})
                      </button>
                    </div>

                    {selectedSiteCode && (
                      <div className="SiteSelectionDiv-siterun">
                        <Box sx={getLabelMinimizeDivSx(theme)}>
                          <Box component="span" sx={getLabelTextSx(theme)}>Timestamp Filter</Box>
                        </Box>
                        <div className="SiteSelectionSelectDiv">
                          <Box sx={{ minWidth: 245 }}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel
                                id="timestamp-select-label"
                                sx={{
                                  fontSize: '12px',
                                  fontFamily: "'Public Sans', sans-serif",
                                  top: '-8px',
                                  '&.MuiInputLabel-shrink': {
                                    top: '0px',
                                  },
                                }}
                              >
                              </InputLabel>
                              <Select
                                labelId="timestamp-select-label"
                                id="timestamp-select"
                                value={selectedRunId}
                                onChange={(e) => setSelectedRunId(e.target.value)}
                                label=""
                                sx={{
                                  fontSize: '12px',
                                  fontFamily: "'Public Sans', sans-serif",
                                  height: '35px',
                                  borderRadius: '8px',
                                  backgroundColor: 'white',
                                  boxShadow: 'none',
                                  '.MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'border',
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'gray' },
                                  '.MuiSelect-select': {
                                    paddingTop: '10px',
                                    paddingBottom: '10px',
                                  },
                                }}
                              >
                                {availableTimestamps.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          {loadingTimestamps && (
                            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                              Loading runs...
                            </span>
                          )}
                        </div>
                      </div>
                    )}


                    {selectedSiteCode && (
                      <div className="SiteSelectionDiv-siterun">
                        <Box sx={getLabelMinimizeDivSx(theme)}>
                          <Box component="span" sx={getLabelTextSx(theme)}>Polygon Visibility</Box>
                        </Box>
                        <div className="SiteSelectionSelectDiv">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={polygonOpacity}
                            onChange={(e) => setPolygonOpacity(parseFloat(e.target.value))}
                            style={{ width: '100%' }}
                          />
                          <span style={{ fontSize: '12px', marginLeft: '10px' }}>
                            {Math.round(polygonOpacity * 100)}%
                          </span>
                        </div>
                      </div>
                    )}



                    <Box sx={getTreeDetailsParentSx(theme)}>
                      <Box sx={getTreeHeadingDivSx(theme)}>
                        <Box component="span" sx={getTreesTextSx(theme)}>Trees</Box>
                        <div className="DataHeadingDiv">
                          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="0.75em" width="0.75em" xmlns="http://www.w3.org/2000/svg" style={{ color: 'rgb(72, 128, 255)', fontSize: 16 }}>
                            <path d="M0 1.5A1.5 1.5 0 0 1 1.5 0h13A1.5 1.5 0 0 1 16 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13zM1.5 1a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5H5V1H1.5zM10 15V1H6v14h4zm1 0h3.5a.5.5 0 0 0 .5-.5v-13a.5.5 0 0 0-.5-.5H11v14z"></path>
                          </svg>
                          <button className="data" onClick={onDataClick}>Data</button>
                        </div>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                              <path d="M16.0001 22.5117C15.6997 22.7312 15.3862 22.9293 15.0626 23.1076V31.9997H16.9376V23.1076C16.6139 22.9293 16.3004 22.7312 16.0001 22.5117Z" fill="currentColor"></path>
                              <path d="M24.6351 7.46663C24.3517 5.506 23.4099 3.70306 21.9453 2.339C20.3258 0.830688 18.2144 0 16.0001 0C13.7858 0 11.6744 0.830688 10.0548 2.339C8.59014 3.70306 7.64845 5.506 7.36495 7.46663C4.21026 8.84413 2.12976 11.9866 2.12976 15.4652C2.12976 20.2764 6.04395 24.1905 10.8551 24.1905C12.338 24.1905 13.7829 23.8128 15.0624 23.108V19.2918L10.8575 17.0809L11.7301 15.4213L15.0626 17.1734V9.32681H16.9376V13.0176L20.2699 11.2654L21.1425 12.925L16.9376 15.136V23.108C18.2171 23.8128 19.662 24.1905 21.1449 24.1905C25.9561 24.1905 29.8703 20.2763 29.8703 15.4652C29.8703 11.9866 27.7898 8.84406 24.6351 7.46663Z" fill="currentColor"></path>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.totalTrees}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>Total Trees/Clusters</Box>
                          </Box>
                        </Box>
                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                              <path d="M2 20 Q2 5 16 5 Q30 5 30 20 Z" fill="currentColor"/>
                              <rect x="14" y="20" width="4" height="7" rx="2" fill="currentColor"/>
                              <line x1="4" y1="27" x2="28" y2="27" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" strokeLinecap="round"/>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.canopyCover}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>Canopy cover area</Box>
                          </Box>
                        </Box>
                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none" style={{ width: 19.5, height: 19.5 }}>
                              <g clipPath="url(#clip0_96_5845)">
                                <path d="M5.66675 8.00391C5.56664 8.07707 5.46212 8.14311 5.35425 8.20253V11.1666H5.97925V8.20253C5.87137 8.14311 5.76685 8.07705 5.66675 8.00391Z" fill="#1779E9"></path>
                                <path d="M8.54513 2.98888C8.45065 2.33533 8.13673 1.73435 7.64852 1.27967C7.10867 0.776896 6.40488 0.5 5.66677 0.5C4.92867 0.5 4.22488 0.776896 3.685 1.27967C3.19679 1.73435 2.8829 2.33533 2.7884 2.98888C1.73684 3.44804 1.04333 4.49552 1.04333 5.65506C1.04333 7.25879 2.34806 8.5635 3.95177 8.5635C4.44608 8.5635 4.92771 8.4376 5.35423 8.20267V6.9306L3.95258 6.19363L4.24346 5.64044L5.35427 6.22448V3.60894H5.97927V4.83919L7.09004 4.25515L7.38092 4.80833L5.97927 5.54533V8.20267C6.40579 8.4376 6.88742 8.5635 7.38173 8.5635C8.98546 8.5635 10.2902 7.25877 10.2902 5.65506C10.2902 4.49552 9.59669 3.44802 8.54513 2.98888Z" fill="#1779E9"></path>
                              </g>
                              <defs>
                                <clipPath id="clip0_96_5845">
                                  <rect width="10.6667" height="10.6667" fill="white" transform="translate(0.333374 0.500977)"></rect>
                                </clipPath>
                              </defs>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.avgTreeHeight || '0 m'}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>Average Tree Height</Box>
                          </Box>
                        </Box>
                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none" style={{ width: 19.5, height: 19.5 }}>
                              <g clipPath="url(#clip0_96_5893)">
                                <path d="M2.92683 8.67967C2.00999 8.11395 1.39893 7.10023 1.39893 5.94379C1.39893 4.16963 2.83718 2.73138 4.61133 2.73138C5.34414 2.73138 6.01961 2.97676 6.56011 3.38979" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M14.9537 6.77527C14.8387 7.78555 14.2551 8.65358 13.4255 9.15667" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M13.9891 8.72266C14.3204 9.228 14.5131 9.83241 14.5131 10.4818C14.5131 12.0757 13.3523 13.3985 11.83 13.6509" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M1.47385 11.9285C0.878504 11.41 0.54716 10.6116 0.66066 9.77074C0.732441 9.23902 0.971191 8.77086 1.31694 8.41199" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M5.74573 2.93741C6.20017 1.92647 7.21598 1.22253 8.39623 1.22253C9.34457 1.22253 10.1868 1.67703 10.7169 2.38013" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M11.0261 12.5427C10.9298 14.2277 9.53143 15.6348 7.82381 15.5643C5.84462 15.4826 5.29387 14.3811 4.39612 13.4526" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M6.62759 9.01259C6.41859 9.16944 6.15884 9.26238 5.8774 9.26238C5.1865 9.26238 4.6264 8.70228 4.6264 8.01138C4.6264 7.32047 5.1865 6.76038 5.8774 6.76038C6.1365 6.76038 6.37719 6.83913 6.57684 6.97403" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M8.56692 9.26238C9.25783 9.26238 9.81792 8.70228 9.81792 8.01138C9.81792 7.32047 9.25783 6.76038 8.56692 6.76038C7.87601 6.76038 7.31592 7.32047 7.31592 8.01138C7.31592 8.70228 7.87601 9.26238 8.56692 9.26238Z" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M10.5452 8.79624C10.5452 8.54868 10.7459 8.34802 10.9934 8.34802C11.241 8.34802 11.4416 8.54871 11.4416 8.79624C11.4416 9.02859 11.3493 9.09809 11.185 9.26237L10.7307 9.63509C10.6132 9.7314 10.5452 9.8753 10.5452 10.0272H11.5021" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M12.0099 2.53105C13.1027 2.13477 13.9812 2.58511 14.3756 3.49174C14.7777 4.41602 14.6736 5.12011 15.3805 5.22096C14.5767 6.40177 11.2672 5.67627 11.4998 3.35658" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M5.94309 15.1354C5.22775 15.4356 4.34575 15.1725 3.80338 14.3459C3.25044 13.5031 3.01319 12.8671 2.29944 12.8875C2.8915 11.5876 6.77356 11.8036 6.61663 14.2465" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M13.8043 4.62394C13.2432 3.67762 12.6463 1.90294 9.83252 2.50591" stroke="#00BB5E" strokeWidth="0.46875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                              </g>
                              <defs>
                                <clipPath id="clip0_96_5893">
                                  <rect width="16" height="16" fill="white" transform="translate(0 0.5)"></rect>
                                </clipPath>
                              </defs>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.carbon || '0 tons'}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>Carbon</Box>
                          </Box>
                        </Box>


                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="green" style={{ width: 16.5, height: 16.5 }}>
                              <path d="M12.7695 8.25781C12.3416 8.25781 11.9642 8.47752 11.7436 8.80991L10.2647 8.44421L11.3917 7.31724C12.2039 7.70664 13.1523 7.11151 13.1523 6.20703C13.1523 5.52855 12.6004 4.97656 11.9219 4.97656C11.2434 4.97656 10.6914 5.52855 10.6914 6.20703C10.6914 6.39677 10.7346 6.57658 10.8117 6.73723L9.18728 8.36158L7.41016 9.77575V7.4719L9.14473 4.93519C9.92668 5.1433 10.6914 4.55025 10.6914 3.74609C10.6914 3.06761 10.1394 2.51562 9.46094 2.51562C8.78246 2.51562 8.23047 3.06761 8.23047 3.74609C8.23047 4.01723 8.31882 4.26803 8.46798 4.47163L7.41016 6.01861V2.39039C7.88755 2.22108 8.23047 1.76518 8.23047 1.23047C8.23047 0.551988 7.67848 0 7 0C6.32152 0 5.76953 0.551988 5.76953 1.23047C5.76953 1.76518 6.11245 2.22108 6.58984 2.39039V6.01861L5.53202 4.47163C5.68118 4.26803 5.76953 4.01723 5.76953 3.74609C5.76953 3.06761 5.21754 2.51562 4.53906 2.51562C3.86058 2.51562 3.30859 3.06761 3.30859 3.74609C3.30859 4.5538 4.07646 5.14246 4.85527 4.93519L6.58984 7.4719V9.77575L4.81261 8.36147L3.18834 6.7372C3.26536 6.57655 3.30859 6.39677 3.30859 6.20703C3.30859 5.52855 2.75661 4.97656 2.07812 4.97656C1.39964 4.97656 0.847656 5.52855 0.847656 6.20703C0.847656 7.11173 1.79643 7.70653 2.60829 7.31724L3.73527 8.44421L2.25635 8.80991C2.03582 8.47752 1.6584 8.25781 1.23047 8.25781C0.551988 8.25781 0 8.8098 0 9.48828C0 10.1668 0.551988 10.7188 1.23047 10.7188C1.8693 10.7188 2.39583 10.2293 2.4552 9.60578L4.44158 9.11457L6.58984 10.8241V13.1797H4.12891V14H9.87109V13.1797H7.41016V10.8241L9.55839 9.11457L11.5448 9.60578C11.6042 10.2293 12.1307 10.7188 12.7695 10.7188C13.448 10.7188 14 10.1668 14 9.48828C14 8.8098 13.448 8.25781 12.7695 8.25781Z" fill="#1779E9"></path>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.newTreesPlanted || '0'}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>New trees planted</Box>
                          </Box>
                        </Box>
                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ width: 16.5, height: 16.5 }}>
                              <path d="M12.7695 8.25781C12.3416 8.25781 11.9642 8.47752 11.7436 8.80991L10.2647 8.44421L11.3917 7.31724C12.2039 7.70664 13.1523 7.11151 13.1523 6.20703C13.1523 5.52855 12.6004 4.97656 11.9219 4.97656C11.2434 4.97656 10.6914 5.52855 10.6914 6.20703C10.6914 6.39677 10.7346 6.57658 10.8117 6.73723L9.18728 8.36158L7.41016 9.77575V7.4719L9.14473 4.93519C9.92668 5.1433 10.6914 4.55025 10.6914 3.74609C10.6914 3.06761 10.1394 2.51562 9.46094 2.51562C8.78246 2.51562 8.23047 3.06761 8.23047 3.74609C8.23047 4.01723 8.31882 4.26803 8.46798 4.47163L7.41016 6.01861V2.39039C7.88755 2.22108 8.23047 1.76518 8.23047 1.23047C8.23047 0.551988 7.67848 0 7 0C6.32152 0 5.76953 0.551988 5.76953 1.23047C5.76953 1.76518 6.11245 2.22108 6.58984 2.39039V6.01861L5.53202 4.47163C5.68118 4.26803 5.76953 4.01723 5.76953 3.74609C5.76953 3.06761 5.21754 2.51562 4.53906 2.51562C3.86058 2.51562 3.30859 3.06761 3.30859 3.74609C3.30859 4.5538 4.07646 5.14246 4.85527 4.93519L6.58984 7.4719V9.77575L4.81261 8.36147L3.18834 6.7372C3.26536 6.57655 3.30859 6.39677 3.30859 6.20703C3.30859 5.52855 2.75661 4.97656 2.07812 4.97656C1.39964 4.97656 0.847656 5.52855 0.847656 6.20703C0.847656 7.11173 1.79643 7.70653 2.60829 7.31724L3.73527 8.44421L2.25635 8.80991C2.03582 8.47752 1.6584 8.25781 1.23047 8.25781C0.551988 8.25781 0 8.8098 0 9.48828C0 10.1668 0.551988 10.7188 1.23047 10.7188C1.8693 10.7188 2.39583 10.2293 2.4552 9.60578L4.44158 9.11457L6.58984 10.8241V13.1797H4.12891V14H9.87109V13.1797H7.41016V10.8241L9.55839 9.11457L11.5448 9.60578C11.6042 10.2293 12.1307 10.7188 12.7695 10.7188C13.448 10.7188 14 10.1668 14 9.48828C14 8.8098 13.448 8.25781 12.7695 8.25781Z" fill="#1779E9"></path>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.naturalCapital || '0'}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>Natural Capital</Box>
                          </Box>
                        </Box>
                        <Box sx={getStatCardEcologicalSx(theme)}>
                          <Box sx={getStatIconWrapperSx(theme)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ width: 16.5, height: 16.5 }}>
                              <path d="M12.7695 8.25781C12.3416 8.25781 11.9642 8.47752 11.7436 8.80991L10.2647 8.44421L11.3917 7.31724C12.2039 7.70664 13.1523 7.11151 13.1523 6.20703C13.1523 5.52855 12.6004 4.97656 11.9219 4.97656C11.2434 4.97656 10.6914 5.52855 10.6914 6.20703C10.6914 6.39677 10.7346 6.57658 10.8117 6.73723L9.18728 8.36158L7.41016 9.77575V7.4719L9.14473 4.93519C9.92668 5.1433 10.6914 4.55025 10.6914 3.74609C10.6914 3.06761 10.1394 2.51562 9.46094 2.51562C8.78246 2.51562 8.23047 3.06761 8.23047 3.74609C8.23047 4.01723 8.31882 4.26803 8.46798 4.47163L7.41016 6.01861V2.39039C7.88755 2.22108 8.23047 1.76518 8.23047 1.23047C8.23047 0.551988 7.67848 0 7 0C6.32152 0 5.76953 0.551988 5.76953 1.23047C5.76953 1.76518 6.11245 2.22108 6.58984 2.39039V6.01861L5.53202 4.47163C5.68118 4.26803 5.76953 4.01723 5.76953 3.74609C5.76953 3.06761 5.21754 2.51562 4.53906 2.51562C3.86058 2.51562 3.30859 3.06761 3.30859 3.74609C3.30859 4.5538 4.07646 5.14246 4.85527 4.93519L6.58984 7.4719V9.77575L4.81261 8.36147L3.18834 6.7372C3.26536 6.57655 3.30859 6.39677 3.30859 6.20703C3.30859 5.52855 2.75661 4.97656 2.07812 4.97656C1.39964 4.97656 0.847656 5.52855 0.847656 6.20703C0.847656 7.11173 1.79643 7.70653 2.60829 7.31724L3.73527 8.44421L2.25635 8.80991C2.03582 8.47752 1.6584 8.25781 1.23047 8.25781C0.551988 8.25781 0 8.8098 0 9.48828C0 10.1668 0.551988 10.7188 1.23047 10.7188C1.8693 10.7188 2.39583 10.2293 2.4552 9.60578L4.44158 9.11457L6.58984 10.8241V13.1797H4.12891V14H9.87109V13.1797H7.41016V10.8241L9.55839 9.11457L11.5448 9.60578C11.6042 10.2293 12.1307 10.7188 12.7695 10.7188C13.448 10.7188 14 10.1668 14 9.48828C14 8.8098 13.448 8.25781 12.7695 8.25781Z" fill="#1779E9"></path>
                            </svg>
                          </Box>
                          <Box sx={getStatInfoDivSx(theme)}>
                            <Box component="span" sx={getStatValueSx(theme)}>
                              {loading ? <Skeleton width={60} /> : stats.siteArea || '0'}
                            </Box>
                            <Box component="span" sx={getStatLabelSx(theme)}>Site Area</Box>
                          </Box>
                        </Box>

                      </Box>
                    </Box>
                  </>
                )}
              </div>
            </div>
          </main>
          {/* Work Orders Panel - Positioned by CSS with 16px gap */}
          <div 
            className={`WorkOrdBarParentSection ${isWorkOrderPanelMinimized ? 'minimized' : ''}`} 
            style={{ zIndex: 'var(--z-panels)' }}
          >
            <div className="WorkOrdBarSubSection">
              {isWorkOrderPanelMinimized ? (
                <Tooltip title="Show Work Orders" placement="left" arrow>
                  <Box 
                    className="minimized-icon-btn"
                    onClick={() => setIsWorkOrderPanelMinimized(false)}
                  >
                    <ClipboardList size={22} color="#47638F" />
                  </Box>
                </Tooltip>
              ) : (
                <>
                  {/* Panel Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    px: 2,
                    py: 1.5,
                    borderBottom: 1, 
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      Work Orders ({(workOrderStats.active ?? workOrderStats.pending ?? 0) + (workOrderStats.completed ?? 0) + (workOrderStats.cancelled ?? 0)})
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {can('Task Management', 'taskAssignment', 'create') && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setCreateTaskOpen(true)}
                          sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: '12px', textTransform: 'none', mr: 0.5 }}
                        >
                          + New
                        </Button>
                      )}
                    {/* Minimize button */}
                    <Box
                      component="span"
                      sx={{ 
                        ...getLabelIconSx(theme),
                        position: 'relative',
                        top: 'auto',
                        right: 'auto'
                      }}
                      onClick={() => setIsWorkOrderPanelMinimized((prev) => !prev)}
                    >
                      <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                        <title>Minimize</title>
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M20 6v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2z"></path>
                        <path d="M20 15h-16"></path>
                        <path d="M14 10l-2 -2l-2 2"></path>
                      </svg>
                    </Box>
                    </Box>
                  </Box>

                  <Box sx={{ px: 1.5, py: 1 }}>
                    {/* View Mode Toggle */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        bgcolor: 'grey.100', 
                        borderRadius: 1, 
                      p: 0.5 
                    }}>
                      <Button
                        size="small"
                        variant={viewMode === 'trees' ? 'contained' : 'text'}
                        onClick={() => setViewMode('trees')}
                        startIcon={<Park sx={{ fontSize: 16 }} />}
                        sx={{ 
                          flex: 1, 
                          textTransform: 'none', 
                          fontSize: '12px',
                          py: 0.5,
                          borderRadius: 0.5,
                          bgcolor: viewMode === 'trees' ? 'primary.main' : 'transparent',
                          color: viewMode === 'trees' ? 'white' : 'text.secondary',
                          '&:hover': { bgcolor: viewMode === 'trees' ? 'primary.dark' : 'grey.200' }
                        }}
                      >
                        Trees
                      </Button>
                      <Button
                        size="small"
                        variant={viewMode === 'grids' ? 'contained' : 'text'}
                        onClick={() => setViewMode('grids')}
                        startIcon={<GridOn sx={{ fontSize: 16 }} />}
                        sx={{ 
                          flex: 1, 
                          textTransform: 'none', 
                          fontSize: '12px',
                          py: 0.5,
                          borderRadius: 0.5,
                          bgcolor: viewMode === 'grids' ? 'primary.main' : 'transparent',
                          color: viewMode === 'grids' ? 'white' : 'text.secondary',
                          '&:hover': { bgcolor: viewMode === 'grids' ? 'primary.dark' : 'grey.200' }
                        }}
                      >
                        Grids
                      </Button>
                      <Button
                        size="small"
                        variant={viewMode === 'maintenance' ? 'contained' : 'text'}
                        onClick={() => setViewMode('maintenance')}
                        startIcon={<Build sx={{ fontSize: 16 }} />}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          fontSize: '12px',
                          py: 0.5,
                          borderRadius: 0.5,
                          bgcolor: viewMode === 'maintenance' ? 'primary.main' : 'transparent',
                          color: viewMode === 'maintenance' ? 'white' : 'text.secondary',
                          '&:hover': { bgcolor: viewMode === 'maintenance' ? 'primary.dark' : 'grey.200' }
                        }}
                      >
                        Maintenance
                      </Button>
                    </Box>
                  </Box>

                  {/* Status Filters */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.75, 
                    mb: 1.5,
                    flexWrap: 'wrap'
                  }}>
                    <Box sx={{ 
                      fontSize: '11px', 
                      fontWeight: 500, 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1,
                      bgcolor: '#dbeafe',
                      color: '#1e40af'
                    }}>
                      Active ({workOrderStats.active ?? workOrderStats.pending ?? 0})
                    </Box>
                    <Box sx={{ 
                      fontSize: '11px', 
                      fontWeight: 500, 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1,
                      bgcolor: '#d1fae5',
                      color: '#065f46'
                    }}>
                      Completed ({workOrderStats.completed ?? 0})
                    </Box>
                    <Box sx={{ 
                      fontSize: '11px', 
                      fontWeight: 500, 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1,
                      bgcolor: '#fee2e2',
                      color: '#991b1b'
                    }}>
                      Cancelled ({workOrderStats.cancelled ?? 0})
                    </Box>
                  </Box>

                  {/* Grid Task Creation Panel - Only visible in grid mode */}
                  {viewMode === 'grids' && (
                    <Box sx={{ mb: 1.5, px: 0.5 }}>
                      <GridTaskPanel
                        siteId={selectedSiteCode}
                        subSiteId={selectedSubSiteId}
                        siteName={selectedSubSiteName || selectedSite}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        selectedGridIds={selectedGridIds}
                        onSelectAllGrids={handleSelectAllGrids}
                        onSelectGridsWithTrees={handleSelectGridsWithTrees}
                        onClearSelection={handleClearGridSelection}
                        totalGridCount={grids.length}
                        gridsWithTreesCount={gridsWithTreesCount}
                        onCreateTasks={handleGridTasksCreated}
                        loading={gridsLoading}
                        selectedTreeIds={selectedTreeIds}
                        selectedTreesData={selectedTreesData}
                        selectedRunId={selectedRunId}
                        selectedHealthFilter={selectedFilter}
                        hideViewToggle={true}
                      />
                    </Box>
                  )}

                  {/* Work Orders List */}
                  <div className="panel-content">
                    <Box sx={getWorkOrderSearchSx(theme)}>
                      <input type="text" placeholder="Search work orders" aria-label="Search work orders" />
                    </Box>
                    <Box sx={getWorkOrderListSx(theme)}>
                      {loadingWorkOrders ? (
                        Array.from({ length: 3 }).map((_, idx) => (
                          <div key={idx} style={{ marginBottom: '12px' }}>
                            <Skeleton height={200} borderRadius={8} />
                          </div>
                        ))
                      ) : (
                        renderWorkOrders()
                      )}
                    </Box>
                  </div>
                </Box>
                </>
              )}
            </div>
          </div>

          {/* Unified Task Modal for Work Order Panel */}
          <UnifiedTaskModal
            open={isWorkOrderTaskModalOpen}
            onClose={handleCloseWorkOrderTaskModal}
            taskId={selectedWorkOrderTaskId}
            onUpdated={() => {
              fetchWorkOrders();
              fetchSiteData();
            }}
          />

          {/* Unified Task Modal for Grid Tasks */}
          <UnifiedTaskModal
            open={gridTaskModalOpen}
            onClose={() => {
              handleCloseGridTaskModal();
              handleClearHighlightedTrees();
            }}
            taskId={selectedGridTaskId}
            onUpdated={() => {
              fetchWorkOrders();
              fetchSiteData();
            }}
          />


          {/* Create Task Dialog (replaces inline form) */}
          <CreateTaskDialog
            open={createTaskOpen}
            onClose={() => setCreateTaskOpen(false)}
            onCreated={(newTaskId) => {
              setCreateTaskOpen(false);
              fetchWorkOrders();
              if (newTaskId) {
                setSelectedWorkOrderTaskId(newTaskId);
                setIsWorkOrderTaskModalOpen(true);
              }
            }}
            prefillSiteId={selectedSiteCode}
            prefillSubSiteId={selectedSubSiteId || undefined}
            restrictAssetTypeId={1}
          />
      </div>
    </Box>
  );
}

export default Ecological;

